export {};

// app.luwu.dev/billing: the plan in force and the terms paid for, what
// happens when it ends (renew for a month or a year, or let it end), cancel
// with a refund, and buying or changing a plan at Luwu Pay; /billing/done is
// where Luwu Pay sends the payer back. Needs the console sign-in (the token
// the account and computers pages keep); otherwise it goes there first.

import { onLuwu } from "./server";

const LUWU = "https://api.luwu.dev";
const TOKEN = "luwu-token";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const get = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};

type PlanId = "basic" | "pro" | "team";
type Period = "month" | "year";
interface Plan {
  id: string;
  name: string;
  computers: number;
  sessions: number;
  clients: number;
  features: string[];
  price: { month: number; year: number } | null;
  seats?: number;
  seat?: { month: number; year: number };
}
interface Entitlement {
  plan: string;
  name: string;
  ends_at: number | null;
}
interface Order {
  id: string;
  plan: string;
  period: Period;
  seats: number;
  amount: number;
  currency: string;
  status: "pending" | "paid" | "expired" | "refunded";
  refunded_amount: number;
  created_at: number;
  paid_at: number | null;
  ends_at?: number | null;
}
interface Term {
  id: string;
  plan: PlanId;
  seats: number;
  starts_at: number;
  ends_at: number;
  order_id: string;
  period: Period;
  amount: number;
  refunded_amount: number;
  paid_at: number | null;
  current: boolean;
  cancellable: boolean;
}
interface Subscription {
  plan: Entitlement & { seats: number };
  team: { id: string; name: string; role: string; active: boolean } | null;
  can_manage: boolean;
  terms: Term[];
  ends_at: number | null;
  renewal: { plan: PlanId; period: Period; seats: number } | null;
  renewal_price: number | null;
}

const token = onLuwu() ? get(TOKEN) : null; // plans are Luwu's; a company server has none here
const q = new URLSearchParams(location.search);
const done = location.pathname.replace(/\/$/, "").endsWith("/done");
const money = (cents: number) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
const day = (s: number | null | undefined) => (s ? new Date(s * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");
const n = (v: number, one: string, many: string) => (v === 0 ? `no limit on ${many}` : v === 1 ? `1 ${one}` : `${v} ${many}`);
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

function view(v: "choose" | "done" | "loading") {
  for (const id of ["choose", "done", "loading"]) $(`v-${id}`).hidden = id !== v;
}

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${LUWU}${path}`, {
    method,
    headers: { authorization: `Bearer ${token}`, ...(body ? { "content-type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) {
    location.replace(`/account?next=${encodeURIComponent(location.pathname + location.search)}`);
    throw new Error("Sign in first.");
  }
  const data = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(data.error ?? `Request failed (${r.status})`);
  return data;
}

let plans: Record<string, Plan> = {};
let sold: string[] = [];
let sub: Subscription;
let plan: PlanId = (["basic", "pro", "team"].includes(q.get("plan") ?? "") ? q.get("plan") : "pro") as PlanId;
let period: Period = q.get("period") === "month" ? "month" : "year";
let seats = Math.max(5, Number(q.get("seats")) || 5);

/** What an order of `p` costs, with Team's extra seats. */
function price(p: PlanId, per: Period, s = 1) {
  const x = plans[p];
  if (!x?.price) return 0;
  let a = x.price[per];
  if (x.seats && x.seat && s > x.seats) a += (s - x.seats) * x.seat[per];
  return a;
}
const planWords = (p: PlanId, per: Period, s = 1) => `${plans[p]?.name ?? p}${p === "team" ? ` for ${s} people` : ""}, 1 ${per}`;

// ---- the plan in force, its terms, renewal ----

function drawSub() {
  const s = sub;
  $("sub-name").textContent = s.plan.name;
  const line: string[] = [];
  if (s.ends_at) line.push(`Paid until ${day(s.ends_at)}.`);
  else if (s.plan.plan === "free") line.push("No paid plan: 1 computer, 2 terminal sessions, 2 phones or browsers.");
  else if (s.plan.plan === "enterprise") line.push("Staff account: every feature, no limits.");
  if (s.team?.active) line.push(`Through ${s.team.name}, where you're ${s.team.role === "member" ? "a member" : `the ${s.team.role}`}.`);
  if (s.ends_at) line.push(s.renewal ? `Renews with ${planWords(s.renewal.plan, s.renewal.period, s.renewal.seats)} when you pay the link.` : "It ends then; nothing renews.");
  $("sub-line").textContent = line.join(" ");

  // Terms paid for: the current one, then any that follow.
  $("terms-box").hidden = !s.terms.length;
  const rows = s.terms.map((t) => {
    const refund = t.starts_at > Date.now() / 1000 ? t.amount - t.refunded_amount : Math.floor(t.amount * Math.min(1, (t.ends_at - Date.now() / 1000) / Math.max(1, t.ends_at - t.starts_at)));
    const btn = t.cancellable && s.can_manage ? `<button class="btn small danger" type="button" data-cancel="${t.order_id}" data-refund="${refund}">Cancel</button>` : "";
    return `<tr><td>${esc(planWords(t.plan, t.period, t.seats))} ${t.current ? `<span class="badge now">Now</span>` : `<span class="badge">Next</span>`}</td><td>${day(t.starts_at)} – ${day(t.ends_at)}</td><td class="num">${money(t.amount)}</td><td class="num">${btn}</td></tr>`;
  });
  $("terms").innerHTML = `<tr><th>Term</th><th>Dates</th><th class="num">Paid</th><th></th></tr>${rows.join("")}`;
  for (const b of $("terms").querySelectorAll<HTMLButtonElement>("[data-cancel]")) b.addEventListener("click", () => cancel(b));

  // Renewal: next month, next year, or let it end. Only with a paid plan in force.
  const paid = !!s.ends_at && s.terms.length > 0;
  $("renew-box").hidden = !paid || !s.can_manage;
  $("managed").hidden = s.can_manage || !s.team;
  if (s.team && !s.can_manage) $("managed").textContent = `${s.team.name}'s owner and admins manage its plan.`;
  if (paid && s.can_manage) {
    const last = s.terms[s.terms.length - 1]!;
    const rp = s.renewal?.plan ?? last.plan;
    const rs = s.renewal?.seats ?? last.seats;
    const opts: { key: string; label: string; price?: number }[] = [
      { key: "month", label: `Renew for 1 month`, price: price(rp, "month", rs) },
      { key: "year", label: `Renew for 1 year`, price: price(rp, "year", rs) },
      { key: "none", label: `Let it end on ${day(s.ends_at)}` },
    ];
    const cur = s.renewal ? s.renewal.period : "none";
    $("renew").innerHTML = opts
      .map(
        (o) =>
          `<label class="choice"><input type="radio" name="renew" value="${o.key}" ${o.key === cur ? "checked" : ""}><span>${esc(o.label)}${o.key !== "none" && rp !== last.plan ? ` (${esc(plans[rp]?.name ?? rp)})` : ""}</span>${o.price ? `<span class="price">${money(o.price)}</span>` : ""}</label>`,
      )
      .join("");
    for (const r of $("renew").querySelectorAll<HTMLInputElement>("input")) r.addEventListener("change", () => setRenewal(r.value as Period | "none", rp, rs));
    const pn = $<HTMLButtonElement>("pay-next");
    pn.hidden = !s.renewal;
    if (s.renewal) {
      pn.textContent = `Pay for the next ${s.renewal.period} now · ${money(s.renewal_price ?? 0)}`;
      pn.onclick = () => checkout(s.renewal!.plan, s.renewal!.period, s.renewal!.seats, pn);
    }
  }

  // Changing plans: a member of a team with a plan doesn't buy their own.
  $("change-box").hidden = !s.can_manage || s.plan.plan === "enterprise";
  $("change-title").textContent = s.terms.length ? "Change plan" : "Choose a plan";
  if (s.team) {
    $("team-name-label").hidden = true;
    if (s.team.active && s.plan.plan === "team") seats = Math.max(seats, s.plan.seats);
  }
}

async function setRenewal(per: Period | "none", p: PlanId, s: number) {
  $("e-sub").textContent = "";
  try {
    if (per === "none") await api("DELETE", "/v1/billing/renewal");
    else await api("PUT", "/v1/billing/renewal", { plan: p, period: per, seats: s });
    sub = await api<Subscription>("GET", "/v1/billing/subscription");
    drawSub();
  } catch (x) {
    $("e-sub").textContent = (x as Error).message;
  }
}

/** Two clicks: the first says what happens, the second does it. */
async function cancel(b: HTMLButtonElement) {
  if (!b.classList.contains("armed")) {
    b.classList.add("armed");
    b.textContent = `Refund ${money(Number(b.dataset.refund))} and end it`;
    setTimeout(() => {
      if (b.isConnected && !b.disabled) {
        b.classList.remove("armed");
        b.textContent = "Cancel";
      }
    }, 6000);
    return;
  }
  b.disabled = true;
  $("e-sub").textContent = "";
  try {
    const r = await api<{ refunded: number }>("POST", `/v1/billing/orders/${b.dataset.cancel}/cancel`);
    await load();
    $("e-sub").textContent = r.refunded ? `Cancelled. ${money(r.refunded)} goes back to how you paid; cards take 5 to 10 working days.` : "Cancelled.";
  } catch (x) {
    $("e-sub").textContent = (x as Error).message;
    b.disabled = false;
  }
}

// ---- choosing a plan ----

function drawPlans() {
  const box = $("plans");
  box.replaceChildren();
  for (const id of sold) {
    const p = plans[id];
    if (!p?.price) continue;
    const label = document.createElement("label");
    label.className = "plan";
    const per = id === "team" ? `<li>${p.seats} people included, then ${money(p.seat![period])} each</li>` : "";
    label.innerHTML = `<input type="radio" name="plan" value="${id}" ${id === plan ? "checked" : ""}><b>${p.name}</b><div class="price">${money(p.price[period])} <span style="font:14px var(--sans);color:var(--muted)">/ ${period}</span></div><ul>${per}<li>${n(p.computers, "computer", "computers")}${id === "team" ? " for the team" : ""}</li><li>${n(p.sessions, "terminal session per computer", "terminal sessions per computer")}</li><li>${n(p.clients, "phone or browser", "phones and browsers")}</li>${p.features.includes("screen") ? "<li>Desktop (screen sharing)</li>" : ""}${p.features.includes("files") ? "<li>Files: browse, preview, upload</li>" : ""}${p.features.includes("team") ? "<li>Share computers with your team</li>" : ""}</ul>`;
    label.querySelector("input")!.addEventListener("change", () => {
      plan = id as PlanId;
      drawSum();
    });
    box.append(label);
  }
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-period]")) b.setAttribute("aria-pressed", String(b.dataset.period === period));
  drawSum();
}

function drawSum() {
  const p = plans[plan];
  if (!p?.price) return;
  $("seats-box").classList.toggle("on", plan === "team");
  $<HTMLInputElement>("seats").value = String(seats);
  $("sum-name").textContent = `Luwu ${planWords(plan, period, seats)}`;
  $("sum-total").textContent = money(price(plan, period, seats));
  history.replaceState(null, "", `/billing?plan=${plan}&period=${period}${plan === "team" ? `&seats=${seats}` : ""}`);
}

function drawOrders(orders: Order[]) {
  const box = $("orders");
  if (!orders.length) {
    box.innerHTML = `<p>None yet.</p>`;
    return;
  }
  const words: Record<Order["status"], string> = { pending: "Waiting for payment", paid: "Paid", expired: "Not paid", refunded: "Refunded" };
  const rows = orders
    .map(
      (o) =>
        `<tr><td>${day(o.created_at)}</td><td>${esc(planWords(o.plan as PlanId, o.period, o.seats))}</td><td>${words[o.status]}${o.refunded_amount && o.status !== "refunded" ? ` (${money(o.refunded_amount)} refunded)` : ""}</td><td class="num">${money(o.amount)}</td><td class="num">${o.status === "paid" || o.status === "refunded" ? `<a href="#" data-invoice="${o.id}">Receipt</a>` : ""}</td></tr>`,
    )
    .join("");
  box.innerHTML = `<div class="table-wrap"><table><tr><th>Date</th><th>Plan</th><th>Status</th><th class="num">Total</th><th></th></tr>${rows}</table></div>`;
  for (const a of box.querySelectorAll<HTMLAnchorElement>("[data-invoice]")) {
    a.addEventListener("click", async (e) => {
      e.preventDefault();
      try {
        const inv = await api<{ invoice: Record<string, unknown>; line: { description: string; amount: number } }>("GET", `/v1/billing/orders/${a.dataset.invoice}/invoice`);
        const w = window.open("", "_blank");
        if (w) {
          w.document.write(`<pre style="font:14px/1.5 ui-monospace,Menlo,monospace;padding:24px">${esc(JSON.stringify({ line: inv.line, ...inv.invoice }, null, 2))}</pre>`);
          w.document.close();
        }
      } catch (x) {
        $("e-choose").textContent = (x as Error).message;
      }
    });
  }
}

async function load() {
  const [catalog, s, orders] = await Promise.all([
    api<{ plans: Record<string, Plan>; sold: string[] }>("GET", "/v1/billing/plans"),
    api<Subscription>("GET", "/v1/billing/subscription"),
    api<{ orders: Order[] }>("GET", "/v1/billing/orders"),
  ]);
  plans = catalog.plans;
  sold = catalog.sold;
  sub = s;
  // Without a plan in the link, the picker starts on the plan in force.
  if (!q.get("plan") && ["basic", "pro", "team"].includes(s.plan.plan)) plan = s.plan.plan as PlanId;
  drawSub();
  drawPlans();
  drawOrders(orders.orders);
  view("choose");
}

async function checkout(p: PlanId, per: Period, s: number, btn: HTMLButtonElement) {
  btn.disabled = true;
  const err = btn.id === "pay" ? $("e-choose") : $("e-sub");
  err.textContent = "";
  try {
    const name = $<HTMLInputElement>("team-name").value.trim();
    const r = await api<{ pay_url: string }>("POST", "/v1/billing/checkout", { plan: p, period: per, ...(p === "team" ? { seats: s, ...(name ? { team_name: name } : {}) } : {}) });
    location.href = r.pay_url;
  } catch (x) {
    err.textContent = (x as Error).message;
    btn.disabled = false;
  }
}

for (const b of document.querySelectorAll<HTMLButtonElement>("[data-period]")) {
  b.addEventListener("click", () => {
    period = b.dataset.period as Period;
    drawPlans();
  });
}
$<HTMLInputElement>("seats").addEventListener("input", (e) => {
  const v = Number((e.target as HTMLInputElement).value);
  if (v >= 5 && v <= 200) {
    seats = Math.floor(v);
    $("sum-name").textContent = `Luwu ${planWords(plan, period, seats)}`;
    $("sum-total").textContent = money(price(plan, period, seats));
  }
});
$<HTMLButtonElement>("pay").addEventListener("click", () => checkout(plan, period, seats, $<HTMLButtonElement>("pay")));

/** /billing/done?order=…: poll until Luwu Pay's webhook has landed (usually seconds). */
async function finish() {
  view("done");
  const id = q.get("order");
  const box = $("status");
  if (!id) {
    box.innerHTML = `<div class="big">No order to check.</div><p><a href="/billing">Your plan</a>.</p>`;
    return;
  }
  const started = Date.now();
  for (;;) {
    let r: { order: Order; term: { ends_at: number } | null; plan: Entitlement };
    try {
      r = await api("GET", `/v1/billing/orders/${id}`);
    } catch (x) {
      box.innerHTML = `<div class="big">Couldn't check the order.</div><p>${esc((x as Error).message)}</p>`;
      return;
    }
    const o = r.order;
    if (o.status === "paid") {
      const team = o.plan === "team" ? ` Invite people on <a href="/team">the Team page</a>.` : "";
      box.innerHTML = `<div class="big ok">Paid. You're on ${esc(r.plan.name)}${r.plan.ends_at ? ` until ${day(r.plan.ends_at)}` : ""}.</div><p>${money(o.amount)} for Luwu ${esc(planWords(o.plan as PlanId, o.period, o.seats))}. The receipt is under Your orders on <a href="/billing">your plan</a>.${team}</p>`;
      return;
    }
    if (o.status === "expired") {
      box.innerHTML = `<div class="big">This order expired unpaid.</div><p><a href="/billing?plan=${o.plan}&period=${o.period}">Start again</a>; nothing was charged.</p>`;
      return;
    }
    if (Date.now() - started > 90_000) {
      box.innerHTML = `<div class="big">Still waiting for the payment.</div><p>If you paid, the plan appears within a few minutes; reload this page, or check <a href="/billing">your plan</a>. If you didn't, nothing was charged.</p>`;
      return;
    }
    await new Promise((res) => setTimeout(res, 3000));
  }
}

if (!token) location.replace(onLuwu() ? `/account?next=${encodeURIComponent(location.pathname + location.search)}` : "/computers");
else if (done) void finish();
else
  load()
    .then(() => {
      if (q.get("cancelled")) $("e-choose").textContent = "The payment was cancelled. Nothing was charged.";
    })
    .catch((x) => {
      view("choose");
      $("e-sub").textContent = (x as Error).message;
    });

// Sign out of the console: forget the token and go to the sign-in page.
document.getElementById("sign-out")?.addEventListener("click", () => {
  try {
    localStorage.removeItem(TOKEN);
  } catch {
    // storage unavailable
  }
  location.replace("/account");
});

// The signed-in person's name in the nav, as the other console pages show it.
async function who() {
  try {
    const me = await api<{ display_name: string | null; username: string | null; email: string | null }>("GET", "/v1/me");
    const el = document.getElementById("who");
    if (el) el.textContent = me.display_name || me.username || me.email || "";
  } catch {
    // just no name
  }
}
if (token) void who();
